import React, { useState, useEffect } from 'react';

interface QuoteItem {
    id: number;
    item_key: string;
    unit_price: string;
    unit: string;
    remarks: string;
}

const QuoteItemManagement: React.FC = () => {
    const [items, setItems] = useState<QuoteItem[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [currentItem, setCurrentItem] = useState<Partial<QuoteItem>>({});

    useEffect(() => {
        fetchItems();
    }, []);

    const fetchItems = async () => {
        try {
            // Public GET
            const responsePublic = await fetch('/api/quote-items');
            if (responsePublic.ok) {
                const data = await responsePublic.json();
                setItems(data);
            }
        } catch (error) {
            console.error('Failed to fetch quote items', error);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return;

        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`/api/admin/quote-items/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                fetchItems();
            } else {
                alert('삭제 실패');
            }
        } catch (error) {
            console.error('Error deleting item:', error);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        const url = isEditing
            ? `/api/admin/quote-items/${currentItem.id}`
            : '/api/admin/quote-items';
        const method = isEditing ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(currentItem)
            });

            if (response.ok) {
                setIsEditing(false);
                setCurrentItem({});
                fetchItems();
            } else {
                alert('저장 실패');
            }
        } catch (error) {
            console.error('Error saving item:', error);
        }
    };

    const startEdit = (item: QuoteItem) => {
        setIsEditing(true);
        setCurrentItem(item);
    };

    const startAdd = () => {
        setIsEditing(false);
        setCurrentItem({});
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;

        const file = e.target.files[0];
        const formData = new FormData();
        formData.append('file', file);

        const token = localStorage.getItem('token');
        try {
            const response = await fetch('/api/admin/quote-items/upload', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (response.ok) {
                const result = await response.json();
                alert(result.message);
                fetchItems();
            } else {
                alert('업로드 실패');
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            alert('업로드 중 오류 발생');
        }

        // Reset input
        e.target.value = '';
    };

    return (
        <div className="quote-item-management" style={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
            <input
                type="file"
                id="excel-upload-input"
                accept=".xlsx, .xls"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
            />

            <div style={{ display: 'flex', gap: '20px', flex: 1, overflow: 'hidden' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h3>항목 목록</h3>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={() => document.getElementById('excel-upload-input')?.click()}
                                className="btn"
                                style={{ background: '#28a745', color: 'white', padding: '5px 10px', fontSize: '14px', cursor: 'pointer' }}
                            >
                                엑셀 업로드
                            </button>
                            <button onClick={startAdd} className="btn" style={{ background: '#6c757d', color: 'white', padding: '5px 10px', fontSize: '14px' }}>초기화</button>
                        </div>
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #eee' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #ddd' }}>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>항목명 (Key)</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>단가</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>단위</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>비고</th>
                                    <th style={{ padding: '10px', width: '100px' }}>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(item => (
                                    <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '10px' }}>{item.item_key}</td>
                                        <td style={{ padding: '10px' }}>{item.unit_price}</td>
                                        <td style={{ padding: '10px' }}>{item.unit}</td>
                                        <td style={{ padding: '10px' }}>{item.remarks}</td>
                                        <td style={{ padding: '10px' }}>
                                            <button onClick={() => startEdit(item)} style={{ marginRight: '5px', border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}>수정</button>
                                            <button onClick={() => handleDelete(item.id)} style={{ border: '1px solid #dc3545', background: '#dc3545', color: 'white', cursor: 'pointer' }}>삭제</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div style={{ width: '300px', padding: '20px', background: '#f8f9fa', borderRadius: '8px', height: 'fit-content' }}>
                    <h3>{isEditing ? '항목 수정' : '새 항목 추가'}</h3>
                    <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '15px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold' }}>항목명 (Key) *</label>
                            <input
                                type="text"
                                value={currentItem.item_key || ''}
                                onChange={e => setCurrentItem({ ...currentItem, item_key: e.target.value })}
                                required
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold' }}>단가</label>
                            <input
                                type="text"
                                value={currentItem.unit_price || ''}
                                onChange={e => setCurrentItem({ ...currentItem, unit_price: e.target.value })}
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold' }}>단위</label>
                            <input
                                type="text"
                                value={currentItem.unit || ''}
                                onChange={e => setCurrentItem({ ...currentItem, unit: e.target.value })}
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold' }}>비고</label>
                            <input
                                type="text"
                                value={currentItem.remarks || ''}
                                onChange={e => setCurrentItem({ ...currentItem, remarks: e.target.value })}
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                                {isEditing ? '수정 저장' : '추가'}
                            </button>
                            {isEditing && (
                                <button type="button" onClick={startAdd} style={{ flex: 1, background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                    취소
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default QuoteItemManagement;
